import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';

const DAY_LABELS = {
  EN: { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' },
  AR: { sunday: 'الأحد', monday: 'الإثنين', tuesday: 'الثلاثاء', wednesday: 'الأربعاء', thursday: 'الخميس', friday: 'الجمعة', saturday: 'السبت' }
};

export default function Step6Availability({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  const [availabilitySchedule, setAvailabilitySchedule] = useState(
    wizardData.availability_schedule || {
      monday: { allDay: false, notAvailable: false, slots: [] },
      tuesday: { allDay: false, notAvailable: false, slots: [] },
      wednesday: { allDay: false, notAvailable: false, slots: [] },
      thursday: { allDay: false, notAvailable: false, slots: [] },
      friday: { allDay: false, notAvailable: false, slots: [] },
      saturday: { allDay: false, notAvailable: false, slots: [] },
      sunday: { allDay: false, notAvailable: false, slots: [] }
    }
  );
  
  const [openTimeDropdown, setOpenTimeDropdown] = useState(null);
  const [errors, setErrors] = useState({});

  // Generate time options (every 30 minutes from 00:00 to 23:30)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const hour12 = hour % 12 || 12;
      const ampm = hour >= 12 
        ? (isArabic ? 'م' : 'PM') 
        : (isArabic ? 'ص' : 'AM');
      const time12 = isArabic 
        ? `${ampm} ${hour12}:${String(minute).padStart(2, '0')}`
        : `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
      timeOptions.push({ value: time24, label: time12 });
    }
  }

  const formatTime = (time24) => {
    if (!time24) return isArabic ? 'ص 9:00' : '9:00 AM';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const hour12 = hour % 12 || 12;
    const ampm = hour >= 12 
      ? (isArabic ? 'م' : 'PM') 
      : (isArabic ? 'ص' : 'AM');
    return isArabic 
      ? `${ampm} ${hour12}:${minutes}`
      : `${hour12}:${minutes} ${ampm}`;
  };

  const addTimeSlot = (day) => {
    setAvailabilitySchedule(prev => ({
      ...prev,
      [day]: {
        allDay: false,
        notAvailable: false,
        slots: [...(prev[day]?.slots || []), { from: '09:00', to: '17:00' }]
      }
    }));
  };

  const removeTimeSlot = (day, index) => {
    setAvailabilitySchedule(prev => {
      const daySlots = prev[day]?.slots || [];
      if (daySlots.length === 1) {
        // If last slot, mark day as not available
        return {
          ...prev,
          [day]: { allDay: false, notAvailable: true, slots: [] }
        };
      }
      return {
        ...prev,
        [day]: {
          ...prev[day],
          slots: daySlots.filter((_, i) => i !== index)
        }
      };
    });
  };

  // Helper to convert 24-hour time to minutes for comparison
  const timeToMinutes = (time24) => {
    if (!time24) return 0;
    const [hours, minutes] = time24.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const updateTimeSlot = (day, index, field, value) => {
    setAvailabilitySchedule(prev => {
      const updatedSlots = (prev[day]?.slots || []).map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      );
      
      // Validate the updated slot
      const updatedSlot = updatedSlots[index];
      const errorKey = `${day}-${index}`;
      
      if (updatedSlot.from && updatedSlot.to) {
        const fromMinutes = timeToMinutes(updatedSlot.from);
        const toMinutes = timeToMinutes(updatedSlot.to);
        
        if (fromMinutes >= toMinutes) {
          // Invalid: from time is not before to time
          setErrors(prevErrors => ({
            ...prevErrors,
            [errorKey]: isArabic 
              ? 'وقت البداية يجب أن يكون قبل وقت النهاية'
              : 'Start time must be before end time'
          }));
        } else {
          // Valid: clear error
          setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[errorKey];
            return newErrors;
          });
        }
      }
      
      return {
        ...prev,
        [day]: {
          ...prev[day],
          allDay: false,
          notAvailable: false,
          slots: updatedSlots
        }
      };
    });
  };

  const toggleDay = (day) => {
    setAvailabilitySchedule(prev => {
      const dayData = prev[day] || { allDay: false, notAvailable: false, slots: [] };
      const isEnabled = !dayData.notAvailable && (dayData.allDay || dayData.slots.length > 0);
      
      if (isEnabled) {
        return {
          ...prev,
          [day]: { allDay: false, notAvailable: true, slots: [] }
        };
      } else {
        return {
          ...prev,
          [day]: { allDay: false, notAvailable: false, slots: [{ from: '09:00', to: '17:00' }] }
        };
      }
    });
  };

  const TimeDropdown = ({ value, onChange, dropdownKey }) => {
    const isOpen = openTimeDropdown === dropdownKey;
    const dropdownRef = useRef(null);
    
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setOpenTimeDropdown(null);
        }
      };
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);
    
    return (
      <div ref={dropdownRef} style={{ position: 'relative', flex: 1 }}>
        <button
          type="button"
          onClick={() => setOpenTimeDropdown(isOpen ? null : dropdownKey)}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: isOpen ? '2px solid #ff7a1a' : '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '15px',
            color: '#1f2937',
            background: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span>{formatTime(value)}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #ff7a1a',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {timeOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpenTimeDropdown(null);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  background: value === opt.value ? '#fff5ed' : '#fff',
                  color: value === opt.value ? '#ff7a1a' : '#374151',
                  fontSize: '14px',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave({ availability_schedule: availabilitySchedule });
    onNext({ availability_schedule: availabilitySchedule });
  };

  const dayLabels = DAY_LABELS[language] || DAY_LABELS.EN;

  return (
    <div>
      <h2 className="step-title">{t.availability || 'Availability'}</h2>
      <p className="step-subtitle">
        {t.availabilitySubtitle || 'Set your weekly availability schedule. Students will see when you\'re available.'}
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        <div style={{
          background: '#f9fafb',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px'
        }}>
          {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => {
            const dayData = availabilitySchedule[day] || { allDay: false, notAvailable: false, slots: [] };
            const daySlots = dayData.slots || [];
            const isEnabled = !dayData.notAvailable && (dayData.allDay || daySlots.length > 0);
            
            return (
              <div key={day} style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: isEnabled ? '20px' : '0' }}>
                  <div
                    onClick={() => toggleDay(day)}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      background: isEnabled ? '#ff7a1a' : '#fff',
                      border: isEnabled ? 'none' : '2px solid #d1d5db',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    {isEnabled && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" />
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: '16px', fontWeight: '500', color: '#1f2937' }}>
                    {dayLabels[day] || day}
                  </span>
                </div>
                
                {isEnabled && (
                  <div style={{ paddingLeft: '36px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {daySlots.map((slot, index) => {
                      const errorKey = `${day}-${index}`;
                      const hasError = errors[errorKey];
                      return (
                        <div key={index}>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <TimeDropdown
                              value={slot.from || '09:00'}
                              onChange={(val) => updateTimeSlot(day, index, 'from', val)}
                              dropdownKey={`${day}-${index}-from`}
                            />
                            <TimeDropdown
                              value={slot.to || '17:00'}
                              onChange={(val) => updateTimeSlot(day, index, 'to', val)}
                              dropdownKey={`${day}-${index}-to`}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                removeTimeSlot(day, index);
                                // Clear error when removing slot
                                setErrors(prevErrors => {
                                  const newErrors = { ...prevErrors };
                                  delete newErrors[errorKey];
                                  return newErrors;
                                });
                              }}
                              style={{
                                padding: '8px',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#dc2626'
                              }}
                            >
                              <FaTimes />
                            </button>
                          </div>
                          {hasError && (
                            <div style={{ 
                              marginTop: '8px', 
                              padding: '8px 12px',
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              color: '#dc2626',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                              </svg>
                              {hasError}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => addTimeSlot(day)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff7a1a',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        textAlign: 'left',
                        textDecoration: 'underline',
                        padding: 0
                      }}
                    >
                      {t.addAnotherTimeslot || (isArabic ? 'إضافة وقت آخر' : 'Add another timeslot')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="wizard-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onBack({ availability_schedule: availabilitySchedule })}>
            {t.back || 'Back'}
          </button>
          <button type="submit" className="btn btn-primary">
            {t.saveAndContinue || 'Save and continue'}
          </button>
        </div>
      </form>
    </div>
  );
}